import React, { useState } from "react";
import axios from "axios";
import styled, { createGlobalStyle, keyframes } from "styled-components";
import { useNavigate } from "react-router-dom";
import SiteFooter from "../components/SiteFooter";
import { CityAutocomplete } from "../components/CityAutocomplete";

const Global = createGlobalStyle`
  @import url('https://fonts.googleapis.com/css2?family=Noto+Serif+KR:wght@400;700;900&display=swap');
  @import url('https://cdn.jsdelivr.net/gh/orioncactus/pretendard/dist/web/static/pretendard.css');
  *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }
  body { background: #FFFBF0; font-family: 'Pretendard','Malgun Gothic','Apple SD Gothic Neo',Arial,sans-serif; }
`;

const API = process.env.REACT_APP_API_URL || "";

const SERVICE_TYPES = [
  "종합운세", "신년운세", "연애운", "결혼운",
  "직업운", "사업운", "건강운", "연애궁합", "심층궁합",
];

/* ─── keyframes ─── */
const floatV = keyframes`
  0%,100% { transform: translateY(0); }
  50%      { transform: translateY(-10px); }
`;

/* ─── layout ─── */
const DancheongBar = styled.div`
  height: 6px;
  width: 100%;
  background: repeating-linear-gradient(90deg,#CC3333 0 20px,#C8901E 20px 40px,#4A7C6B 40px 60px,#2E6DA4 60px 80px,#C8901E 80px 100px);
`;

const PageWrap = styled.div`
  max-width: 520px;
  margin: 0 auto;
  padding: 0 16px 80px;
  background: #FFFBF0;
`;

/* 전체 씬 히어로 */
const HeroWrap = styled.div`
  width: calc(100% + 32px);
  margin-left: -16px;
  position: relative;
  overflow: hidden;
  margin-bottom: 24px;
`;
const HeroImg = styled.img`
  width: 100%;
  display: block;
  object-fit: cover;
`;
const HeroOverlay = styled.div`
  position: absolute; bottom: 0; left: 0; right: 0; height: 120px;
  background: linear-gradient(to bottom, transparent, #FFFBF0);
`;
const HeroTitle = styled.h1`
  position: absolute; bottom: 22px; left: 0; right: 0;
  text-align: center;
  font-family: 'Noto Serif KR', serif;
  font-size: 1.5rem; font-weight: 900; color: #2C1810;
  text-shadow: 0 2px 8px rgba(255,251,240,0.8);
`;
const HeroSub = styled.p`
  position: absolute; bottom: 4px; left: 0; right: 0;
  text-align: center;
  font-size: 0.78rem; color: #7A5C42; font-weight: 600;
  text-shadow: 0 1px 4px rgba(255,251,240,0.9);
`;

const FormCard = styled.div`
  background: #fff;
  border-radius: 20px;
  border: 1.5px solid #E8D5B5;
  padding: 28px 24px;
  box-shadow: 0 8px 36px rgba(200,144,30,0.10), 0 2px 8px rgba(0,0,0,0.04);
`;

const TwoCol = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 8px;
`;

const CompatBand = styled.div`
  margin-bottom: 14px;
  padding: 16px;
  background: #FFF5F8;
  border: 1.5px solid #FFD6E5;
  border-radius: 14px;
`;
const CompatBandTitle = styled.div`
  font-size: 0.8rem;
  font-weight: 700;
  color: #C2255C;
  margin-bottom: 14px;
`;

const SubmitBtn = styled.button`
  width: 100%;
  padding: 15px;
  background: linear-gradient(135deg, #C8901E, #A07020);
  color: #fff;
  border: none;
  border-radius: 14px;
  font-family: 'Noto Serif KR', serif;
  font-size: 1rem;
  font-weight: 900;
  cursor: pointer;
  box-shadow: 0 6px 20px rgba(200,144,30,0.35);
  transition: opacity 0.2s, transform 0.15s;
  letter-spacing: -0.02em;
  &:hover { opacity: 0.92; transform: translateY(-1px); }
  &:active { transform: translateY(0); }
  &:disabled { background: #ccc; cursor: not-allowed; box-shadow: none; }
`;

const ErrBox = styled.div`
  padding: 10px 14px;
  background: #fff5f5;
  border: 1px solid #ffc9c9;
  border-radius: 10px;
  font-size: 0.82rem;
  color: #c92a2a;
  margin-bottom: 14px;
`;

const NavRow = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: 16px;
`;
const BackBtn = styled.button`
  display: flex;
  align-items: center;
  gap: 6px;
  background: rgba(255,255,255,0.8);
  border: 1.5px solid #e0d5c8;
  border-radius: 20px;
  padding: 7px 14px;
  cursor: pointer;
  font-size: 13px;
  font-weight: 700;
  color: #7a6654;
  font-family: inherit;
  box-shadow: 0 2px 8px rgba(100,70,40,0.08);
`;
const PageNameTag = styled.div`
  flex: 1;
  text-align: right;
  font-family: 'Noto Serif KR', serif;
  font-size: 15px;
  font-weight: 900;
  color: #2C1810;
`;

const InfoBox = styled.div`
  margin-top: 16px;
  padding: 14px 16px;
  background: #FFF8EE;
  border-radius: 14px;
  border: 1px solid #E8D5B5;
  font-size: 0.78rem;
  color: #9c7b5a;
  line-height: 1.8;
`;

/* ─── 인라인 스타일 → styled 대체용 ─── */
const inputStyle: React.CSSProperties = {
  width: "100%", padding: "10px 12px", border: "1.5px solid #e0d5c8",
  borderRadius: 10, fontSize: "0.88rem", color: "#2C1810",
  background: "#fdfaf7", fontFamily: "inherit", marginBottom: 14,
};
const inputDisabledStyle: React.CSSProperties = {
  ...inputStyle, background: "#f5f0ea", color: "#b0a090", cursor: "not-allowed",
};
const labelStyle: React.CSSProperties = {
  display: "block", fontSize: "0.78rem", fontWeight: 700, color: "#7a6654", marginBottom: 6,
};

function parseTime(val: string) {
  const s = val.replace(/\s/g, "");
  if (!s) return { hour: 99, minute: 0 };
  let h: number, m: number;
  if (s.includes(":")) {
    const parts = s.split(":");
    h = parseInt(parts[0]); m = parseInt(parts[1] || "0");
  } else if (/^\d{3,4}$/.test(s)) {
    const p = s.padStart(4, "0");
    h = parseInt(p.slice(0, 2)); m = parseInt(p.slice(2));
  } else {
    h = parseInt(s); m = 0;
  }
  return {
    hour: (!isNaN(h) && h >= 0 && h <= 23) ? h : 99,
    minute: (!isNaN(m) && m >= 0 && m <= 59) ? m : 0,
  };
}

function parseBirthDate(val: string) {
  const sep = val.includes("/") ? "/" : val.includes(".") ? "." : val.includes("-") ? "-" : null;
  if (sep) {
    const [y, m, d] = val.split(sep).map(Number);
    return { year: y || 0, month: m || 0, day: d || 0 };
  }
  if (val.length === 8) {
    return { year: Number(val.slice(0,4)), month: Number(val.slice(4,6)), day: Number(val.slice(6,8)) };
  }
  return { year: 0, month: 0, day: 0 };
}

const COMPAT_SERVICES = ["연애궁합", "심층궁합", "재회운", "결혼운"];

const emptyForm = () => ({
  name: "", email: "", phone: "", gender: "female",
  birth_date: "", birth_type: "solar",
  birth_time: "", birth_time_unknown: false,
  city: "서울특별시",
  service_type: "종합운세", memo: "", consent: false,
});

const emptyPartner = () => ({
  name: "", gender: "female",
  birth_date: "", birth_type: "solar",
  birth_time: "", birth_time_unknown: false,
  city: "서울특별시",
});

export default function VisitPage() {
  const navigate = useNavigate();
  const [form, setForm] = useState(emptyForm());
  const [partner, setPartner] = useState(emptyPartner());
  const [loading, setLoading] = useState(false);
  const [error,   setError]   = useState("");
  const [done,    setDone]    = useState(false);

  const isCompat = COMPAT_SERVICES.includes(form.service_type);

  const set = (k: string) => (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) =>
    setForm(f => ({ ...f, [k]: e.target.value }));
  const setP = (k: string) => (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) =>
    setPartner(p => ({ ...p, [k]: e.target.value }));

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.consent) { setError("개인정보 활용에 동의해주세요."); return; }
    if (!form.name || !form.birth_date) {
      setError("필수 항목(이름, 생년월일)을 모두 입력해주세요."); return;
    }
    const { year, month, day } = parseBirthDate(form.birth_date);
    if (!year || !month || !day) {
      setError("생년월일을 YYYY/MM/DD 형식으로 입력해주세요. 예) 1990/05/15"); return;
    }
    if (isCompat) {
      const pd = parseBirthDate(partner.birth_date);
      if (!partner.name || !pd.year) {
        setError("상대방 이름과 생년월일을 입력해주세요."); return;
      }
    }
    setLoading(true); setError("");
    const pd = parseBirthDate(partner.birth_date);
    const partnerPayload = isCompat ? {
      name: partner.name,
      gender: partner.gender,
      birth_year: pd.year, birth_month: pd.month, birth_day: pd.day,
      birth_hour: partner.birth_time_unknown ? 99 : parseTime(partner.birth_time).hour,
      birth_minute: partner.birth_time_unknown ? 0 : parseTime(partner.birth_time).minute,
      birth_type: partner.birth_type,
      city: partner.city,
      compatibility_type: form.service_type === "심층궁합" ? "deep" : "casual",
    } : null;
    try {
      await axios.post(`${API}/api/v1/visit/submit`, {
        name: form.name,
        email: form.email,
        phone: form.phone,
        gender: form.gender,
        birth_year: year, birth_month: month, birth_day: day,
        birth_hour: form.birth_time_unknown ? 99 : parseTime(form.birth_time).hour,
        birth_minute: form.birth_time_unknown ? 0 : parseTime(form.birth_time).minute,
        birth_type: form.birth_type,
        city: form.city,
        service_type: form.service_type,
        partner_info: partnerPayload,
        memo: form.memo,
        consent: true,
      });
      setDone(true);
    } catch (err: any) {
      setError(err.response?.data?.detail || "제출 중 오류가 발생했습니다.");
    } finally {
      setLoading(false);
    }
  };

  if (done) {
    return (
      <>
        <Global />
        <DancheongBar />
        <PageWrap>
          <HeroWrap>
            <HeroImg src="/woo_02.png" alt="우연" />
            <HeroTitle>우연 사주</HeroTitle>
          </HeroWrap>
          <FormCard style={{ textAlign: "center" }}>
            <div style={{ fontSize: "2.4rem", marginBottom: 12 }}>✅</div>
            <div style={{ fontFamily: "'Noto Serif KR', serif", fontSize: "1.1rem", fontWeight: 900, color: "#2C1810", marginBottom: 10 }}>
              방문 상담 접수 완료
            </div>
            <div style={{ fontSize: "0.85rem", color: "#7a6654", lineHeight: 1.8 }}>
              접수가 완료되었습니다.<br />
              {form.email && <>상담 후 분석 리포트를 <strong>{form.email}</strong>으로 보내드리겠습니다.<br /></>}
              문의: wooyeonsaju@gmail.com
            </div>
            <SubmitBtn
              onClick={() => { setDone(false); setForm(emptyForm()); setPartner(emptyPartner()); }}
              style={{ marginTop: 20 }}
            >
              새 접수하기
            </SubmitBtn>
          </FormCard>
        </PageWrap>
      </>
    );
  }

  return (
    <>
      <Global />
      <DancheongBar />
      <PageWrap>
        <NavRow>
          <BackBtn onClick={() => navigate("/")}>← 홈</BackBtn>
          <PageNameTag>우연이와 1:1 방문 상담</PageNameTag>
        </NavRow>

        <HeroWrap>
          <HeroImg src="/woo_02.png" alt="우연이와 1:1 방문 상담" />
          <HeroOverlay />
          <HeroTitle>우연이와 1:1 방문 상담</HeroTitle>
          <HeroSub>대면 상담 후 명리학 분석 리포트 이메일 발송</HeroSub>
        </HeroWrap>

        <FormCard>
          <div style={{ display: "inline-block", padding: "5px 14px", background: "#C8901E18", border: "1.5px solid #C8901E40", borderRadius: 20, fontSize: "0.75rem", fontWeight: 700, color: "#C8901E", marginBottom: 20 }}>
            방문 상담 — 상담 후 리포트를 이메일로 발송해드립니다
          </div>

          <form onSubmit={handleSubmit}>
            {/* 이름 + 성별 */}
            <TwoCol>
              <div>
                <label style={labelStyle}>이름 *</label>
                <input style={inputStyle} value={form.name} onChange={set("name")} placeholder="홍길동" required />
              </div>
              <div>
                <label style={labelStyle}>성별 *</label>
                <select style={inputStyle} value={form.gender} onChange={set("gender")}>
                  <option value="female">여성</option>
                  <option value="male">남성</option>
                </select>
              </div>
            </TwoCol>

            {/* 생년월일 — 한 칸 */}
            <label style={labelStyle}>생년월일 * (예: 1990/05/15)</label>
            <input style={inputStyle} value={form.birth_date} onChange={set("birth_date")} placeholder="1990/05/15" maxLength={10} />

            {/* 양음력 */}
            <label style={labelStyle}>양/음력</label>
            <select style={inputStyle} value={form.birth_type} onChange={set("birth_type")}>
              <option value="solar">양력</option>
              <option value="lunar">음력</option>
              <option value="lunar_leap">음력 윤달</option>
            </select>

            {/* 태어난 시간 */}
            <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8 }}>
              <label style={{ ...labelStyle, marginBottom: 0 }}>태어난 시간</label>
              <label style={{ display: "flex", alignItems: "center", gap: 4, fontSize: "0.78rem", color: "#7a6654", cursor: "pointer" }}>
                <input type="checkbox" checked={form.birth_time_unknown}
                  onChange={e => setForm(f => ({ ...f, birth_time_unknown: e.target.checked }))}
                  style={{ accentColor: "#C8901E" }} />
                시간 모름
              </label>
            </div>
            <input
              style={form.birth_time_unknown ? inputDisabledStyle : inputStyle}
              placeholder="예: 14:30 또는 1430"
              maxLength={5}
              value={form.birth_time_unknown ? "" : form.birth_time}
              disabled={form.birth_time_unknown}
              onChange={set("birth_time")} />

            {/* 출생 도시 */}
            <label style={labelStyle}>출생 도시 (시간 보정용)</label>
            <div style={{ marginBottom: 14 }}>
              <CityAutocomplete value={form.city} onChange={v => setForm(f => ({ ...f, city: v }))} />
            </div>

            {/* 이메일 + 전화 */}
            <label style={labelStyle}>이메일 (리포트 수령용, 선택)</label>
            <input style={inputStyle} type="email" value={form.email} onChange={set("email")} placeholder="example@email.com" />
            <label style={labelStyle}>전화번호</label>
            <input style={inputStyle} value={form.phone} onChange={set("phone")} placeholder="010-0000-0000" />

            {/* 서비스 종류 */}
            <label style={labelStyle}>상담 종류 *</label>
            <select style={inputStyle} value={form.service_type} onChange={set("service_type")}>
              {SERVICE_TYPES.map(s => <option key={s} value={s}>{s}</option>)}
            </select>

            {/* 상대방 정보 (궁합 서비스 선택 시) */}
            {isCompat && (
              <CompatBand>
                <CompatBandTitle>💕 상대방 정보 입력</CompatBandTitle>
                <TwoCol>
                  <div>
                    <label style={labelStyle}>상대방 이름 *</label>
                    <input style={inputStyle} value={partner.name} onChange={setP("name")} placeholder="홍길동" />
                  </div>
                  <div>
                    <label style={labelStyle}>상대방 성별 *</label>
                    <select style={inputStyle} value={partner.gender} onChange={setP("gender")}>
                      <option value="female">여성</option>
                      <option value="male">남성</option>
                    </select>
                  </div>
                </TwoCol>
                <label style={labelStyle}>상대방 생년월일 * (예: 1990/05/15)</label>
                <input style={inputStyle} value={partner.birth_date} onChange={setP("birth_date")} placeholder="1990/05/15" maxLength={10} />
                <label style={labelStyle}>상대방 양/음력</label>
                <select style={inputStyle} value={partner.birth_type} onChange={setP("birth_type")}>
                  <option value="solar">양력</option>
                  <option value="lunar">음력</option>
                  <option value="lunar_leap">음력 윤달</option>
                </select>
                <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8 }}>
                  <label style={{ ...labelStyle, marginBottom: 0 }}>상대방 태어난 시간</label>
                  <label style={{ display: "flex", alignItems: "center", gap: 4, fontSize: "0.78rem", color: "#7a6654", cursor: "pointer" }}>
                    <input type="checkbox" checked={partner.birth_time_unknown}
                      onChange={e => setPartner(p => ({ ...p, birth_time_unknown: e.target.checked }))}
                      style={{ accentColor: "#C8901E" }} />
                    시간 모름
                  </label>
                </div>
                <input
                  style={partner.birth_time_unknown ? inputDisabledStyle : inputStyle}
                  placeholder="예: 14:30 또는 1430"
                  maxLength={5}
                  value={partner.birth_time_unknown ? "" : partner.birth_time}
                  disabled={partner.birth_time_unknown}
                  onChange={setP("birth_time")} />
                <label style={labelStyle}>상대방 출생 도시</label>
                <div style={{ marginBottom: 14 }}>
                  <CityAutocomplete value={partner.city} onChange={v => setPartner(p => ({ ...p, city: v }))} />
                </div>
              </CompatBand>
            )}

            {/* 메모 */}
            <label style={labelStyle}>상담 요청 내용 (선택)</label>
            <textarea style={{ ...inputStyle, height: 80, resize: "vertical" } as React.CSSProperties}
              value={form.memo} onChange={set("memo")}
              placeholder="궁금하신 점이나 원하시는 상담 내용을 간단히 적어주세요." />

            {/* 동의 */}
            <label style={{ display: "flex", alignItems: "flex-start", gap: 8, fontSize: "0.78rem", color: "#7a6654", marginBottom: 20, cursor: "pointer" }}>
              <input type="checkbox" checked={form.consent}
                onChange={e => setForm(f => ({ ...f, consent: e.target.checked }))}
                style={{ marginTop: 2, accentColor: "#C8901E" }} />
              <span>개인정보(생년월일, 이메일 등)를 사주 분석 및 서비스 제공 목적으로 수집·이용하는 것에 동의합니다. *</span>
            </label>

            {error && <ErrBox>{error}</ErrBox>}

            <SubmitBtn type="submit" disabled={loading}>
              {loading ? "접수 중..." : "방문 상담 접수하기"}
            </SubmitBtn>
          </form>
        </FormCard>

        <InfoBox>
          · 접수 후 상담 시 추가 정보를 확인할 수 있습니다.<br />
          · 분석 리포트는 상담 완료 후 이메일로 발송됩니다.<br />
          · 문의: wooyeonsaju@gmail.com
        </InfoBox>
        <SiteFooter />
      </PageWrap>
    </>
  );
}
